import React, { useEffect, useMemo, useState } from "react";
import { api, statusPill, statusLabel } from "../lib/api";
import { ChevronLeft, ChevronRight, Calendar as CalIcon } from "lucide-react";
import { toast } from "sonner";

const DAY_NAMES = ["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"];
const MONTH_NAMES = ["Ocak","Şubat","Mart","Nisan","Mayıs","Haziran","Temmuz","Ağustos","Eylül","Ekim","Kasım","Aralık"];

const ymd = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function CalendarPage() {
  const [cursor, setCursor] = useState(new Date());
  const [cases, setCases] = useState([]);
  const [dragging, setDragging] = useState(null);

  const load = async () => {
    const r = await api.get("/cases");
    setCases(r.data);
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const grid = useMemo(() => {
    const y = cursor.getFullYear();
    const m = cursor.getMonth();
    const first = new Date(y, m, 1);
    // Monday = 0 index
    const offset = (first.getDay() + 6) % 7;
    const startDate = new Date(y, m, 1 - offset);
    return Array.from({ length: 42 }, (_, i) => {
      const d = new Date(startDate);
      d.setDate(startDate.getDate() + i);
      return d;
    });
  }, [cursor]);

  const byDay = useMemo(() => {
    const map = {};
    cases.forEach((c) => {
      if (!c.appointment_date) return;
      const k = c.appointment_date.slice(0, 10);
      (map[k] = map[k] || []).push(c);
    });
    return map;
  }, [cases]);

  const onDrop = async (day) => {
    if (!dragging) return;
    const newDate = ymd(day);
    try {
      // fetch full record then PUT
      const r = await api.get(`/cases/${dragging.id}`);
      await api.put(`/cases/${dragging.id}`, { ...r.data, appointment_date: newDate });
      toast.success(`${dragging.full_name} → ${newDate}`);
      load();
    } catch { toast.error("Güncellenemedi"); }
    setDragging(null);
  };

  const month = MONTH_NAMES[cursor.getMonth()];
  const year = cursor.getFullYear();

  return (
    <div className="p-6 md:p-8 max-w-[1400px]" data-testid="calendar-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <CalIcon size={22} />
          <div>
            <div className="label-overline">Randevu Takvimi</div>
            <h1 className="mt-1 font-display font-black text-3xl md:text-4xl tracking-tight">{month} {year}</h1>
          </div>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setCursor(new Date(year, cursor.getMonth() - 1, 1))} className="h-10 w-10 border border-zinc-200 rounded-md hover:bg-zinc-100 flex items-center justify-center" data-testid="cal-prev"><ChevronLeft size={16} /></button>
          <button onClick={() => setCursor(new Date())} className="h-10 px-4 border border-zinc-200 rounded-md text-sm hover:bg-zinc-100" data-testid="cal-today">Bugün</button>
          <button onClick={() => setCursor(new Date(year, cursor.getMonth() + 1, 1))} className="h-10 w-10 border border-zinc-200 rounded-md hover:bg-zinc-100 flex items-center justify-center" data-testid="cal-next"><ChevronRight size={16} /></button>
        </div>
      </div>

      <p className="mt-2 text-xs text-zinc-500">💡 Randevu kartını başka bir güne <b>sürükleyerek</b> tarihi değiştirebilirsiniz.</p>

      <div className="mt-6 grid-card overflow-hidden">
        <div className="grid grid-cols-7 bg-zinc-50 border-b border-zinc-200">
          {DAY_NAMES.map((d) => (
            <div key={d} className="px-3 py-2 label-overline">{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {grid.map((d) => {
            const inMonth = d.getMonth() === cursor.getMonth();
            const today = ymd(d) === ymd(new Date());
            const items = byDay[ymd(d)] || [];
            const dayKey = ymd(d);
            return (
              <div
                key={dayKey}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => onDrop(d)}
                className={`min-h-[120px] p-2 border-r border-b border-zinc-100 ${inMonth ? "bg-white" : "bg-zinc-50/60"} ${today ? "ring-2 ring-inset ring-[#0033cc]/40" : ""}`}
                data-testid={`cal-day-${ymd(d)}`}
              >
                <div className={`text-xs mb-1 ${inMonth ? "text-zinc-700 font-semibold" : "text-zinc-400"} ${today ? "text-[#0033cc]" : ""}`}>{d.getDate()}</div>
                <div className="space-y-1">
                  {items.map((c) => (
                    <div
                      key={c.id}
                      draggable
                      onDragStart={() => setDragging(c)}
                      onDragEnd={() => setDragging(null)}
                      className="cursor-grab active:cursor-grabbing bg-white border border-zinc-200 hover:border-zinc-950 rounded p-1.5 text-[11px] shadow-sm"
                      title={`${c.full_name} · ${c.appointment_time || ""} · ${c.country_name || ""}`}
                      data-testid={`cal-event-${c.id}`}
                    >
                      <div className="font-semibold truncate">{c.appointment_time || "—"} · {c.full_name}</div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className={`status-pill ${statusPill(c.status)} !py-0 !px-1 !text-[9px]`}>{statusLabel(c.status)}</span>
                        <span className="truncate text-zinc-500">{c.country_name}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
